<template>
  <Transition name="toast">
    <div
      v-if="isVisible"
      :class="['error-toast', { 'has-bold-text': hasBoldText }]"
      :style="[baseStyles, customStyles]"
    >
      <div class="toast-content">
        <span class="toast-text">
          {{ text }}
          <b
            v-if="hasBoldText"
            class="clickable-text"
            @click="handleBoldTextClick"
          >
            {{ boldText }}
          </b>
        </span>
        <button
          class="close-button"
          aria-label="Close error message"
          @click="closeToast"
        >
          <span class="close-icon">×</span>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import Segment from '../../Segment';
import DDLogs from '../../DDLogs';
import { useSessionStore } from '../../stores/session';

interface Props {
  text?: string;
  boldText?: string;
  color?: string;
  styles?: Record<string, string>;
  reason?: string;
}

const props = withDefaults(defineProps<Props>(), {
  text: 'Something went wrong. Please try again.',
  boldText: '',
  color: '#EF3C3C',
  styles: () => ({}),
  reason: 'unknown',
});

const emit = defineEmits<{
  close: [];
  boldTextClick: [];
}>();

const sessionStore = useSessionStore();
const isVisible = ref(false);
const hasBoldText = computed(() => !!props.boldText);

const baseStyles = computed(() => ({
  backgroundColor: props.color,
}));

const customStyles = computed(() => props.styles);

const closeToast = () => {
  isVisible.value = false;
  emit('close');
};

const handleBoldTextClick = () => {
  emit('boldTextClick');
};

const showToast = () => {
  isVisible.value = true;
  Segment.trackUserGot('ErrorToastShown', {
    reason: props.reason,
  });
  DDLogs.error('ErrorToastShown', {
    reason: props.reason,
    customerId: sessionStore.customerId || 'not_authenticated',
  });

  // Auto-hide after 10 seconds
  setTimeout(() => {
    if (isVisible.value) {
      closeToast();
    }
  }, 10000);
};

onMounted(() => {
  showToast();
});

defineExpose({
  showToast,
  closeToast,
});
</script>

<style lang="scss" scoped>
.error-toast {
  position: absolute;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 400px;
  padding: 16px 20px;
  color: white;
  font-family: CircularStdBook;
  font-size: 14px;
  line-height: 1.4;

  .toast-content {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .toast-text {
    flex: 1;
    word-wrap: break-word;
  }

  .clickable-text {
    cursor: pointer;
    text-decoration: underline;
    font-weight: 600;

    &:hover {
      opacity: 0.8;
    }
  }

  .close-button {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }

    &:focus {
      outline: 2px solid rgba(255, 255, 255, 0.5);
      outline-offset: 2px;
    }
  }

  .close-icon {
    font-size: 18px;
    font-weight: bold;
    line-height: 1;
  }
}

// Transition animations
.toast-enter-active,
.toast-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(-100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-100%);
}
</style>
